import './ModifiedDropdown.less';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import _ from 'lodash';
import React, {
  type CSSProperties,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dropdown,
  type DropdownItemProps,
  Icon,
  Label,
  type LabelProps,
  type StrictDropdownProps,
} from 'semantic-ui-react';

import {IconChevronDown, IconChevronUp} from '../../../components/Icon';
import {usePrevious} from '../../state/hooks';
import {type Omit} from '../../types/base';
import {type Option} from '../../util/uihelpers';

const ITEM_LIMIT_VALUE = '__item_limit';

/**
 * The functionality here is similar to `searchRegexFromQuery` in `panelbank.ts`
 */
export function getAsValidRegex(s: string): RegExp | null {
  let cleanS = s.trim();

  // if the query is a single '*', match everything (even though * isn't technically a valid regex)
  if (cleanS === '*') {
    cleanS = '.*';
  }

  if (cleanS.length === 0) {
    return null;
  }

  try {
    return new RegExp(cleanS, 'i');
  } catch (e) {
    return null;
  }
}

export const simpleSearch = (
  options: DropdownItemProps[],
  query: string,
  config: {
    allowRegexSearch?: boolean;
  } = {}
) => {
  const regex = config.allowRegexSearch ? getAsValidRegex(query) : null;

  return _.chain(options)
    .filter(o => {
      const t = typeof o.text === 'string' ? o.text : JSON.stringify(o.text);

      return regex
        ? regex.test(t)
        : _.includes(t.toLowerCase(), query.toLowerCase());
    })
    .sortBy(o => {
      const oString =
        typeof o.text === 'string' ? o.text : JSON.stringify(o.text);
      const qString = typeof query === 'string' ? query : JSON.stringify(query);

      return oString.toLowerCase() === qString.toLowerCase() ? 0 : 1;
    })
    .value();
};

const getOptionProps = (opt: Option, hideText: boolean) => {
  const {text, ...props} = opt;
  props.text = hideText ? null : opt.text;
  // props.text = hideText ? null : (
  //   <OptionWithTooltip text={opt.text as string} />
  // );
  return props;
};

export interface ModifiedDropdownExtraProps {
  perfAction?: string;
  allowRegexSearch?: boolean;
  debounceTime?: number;
  enableReordering?: boolean;
  hideText?: boolean;
  itemLimit?: number;
  options: Option[];
  optionTransform?(option: Option): Option;
  resultLimit?: number;
  resultLimitMessage?: string;
  style?: CSSProperties;
  useIcon?: boolean;
}

type ModifiedDropdownProps = Omit<StrictDropdownProps, 'options'> &
  ModifiedDropdownExtraProps;

export function dropdownOptionsAreShallowEqual(
  prevOptions: Option[],
  nextOptions: Option[]
) {
  if (prevOptions === nextOptions) {
    return true;
  }
  if (prevOptions.length !== nextOptions.length) {
    return false;
  }

  for (let i = 0; i < prevOptions.length; i++) {
    const prevOption = prevOptions[i] as Record<string, unknown>;
    const nextOption = nextOptions[i] as Record<string, unknown>;
    if (prevOption === nextOption) {
      continue;
    }

    const nextOptionKeys = Object.keys(nextOption);
    if (Object.keys(prevOption).length !== nextOptionKeys.length) {
      return false;
    }
    for (const key of nextOptionKeys) {
      if (!Object.prototype.hasOwnProperty.call(prevOption, key)) {
        return false;
      }
      if (prevOption[key] !== nextOption[key]) {
        return false;
      }
    }
  }

  return true;
}

export function modifiedDropdownPropsAreEqual(
  prevProps: ModifiedDropdownProps,
  nextProps: ModifiedDropdownProps
) {
  const nextKeys = Object.keys(nextProps) as Array<keyof ModifiedDropdownProps>;
  if (Object.keys(prevProps).length !== nextKeys.length) {
    return false;
  }

  let shouldCompareOptions = false;
  for (const key of nextKeys) {
    if (!Object.prototype.hasOwnProperty.call(prevProps, key)) {
      return false;
    }
    if (key === 'options') {
      shouldCompareOptions = true;
    } else if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }

  return (
    !shouldCompareOptions ||
    dropdownOptionsAreShallowEqual(prevProps.options, nextProps.options)
  );
}

const SortableLabel: FC<{id: string; children: React.ReactNode}> = ({
  id,
  children,
}) => {
  const {attributes, isDragging, listeners, setNodeRef, transform, transition} =
    useSortable({id});

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        display: 'inline-block',
        margin: '.125rem .25rem .125rem 0',
        maxWidth: '100%',
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        // translate-only (not transform) so dnd-kit's scaleX/scaleY don't
        // squash the dragged pill to a neighbor's width
        transform: CSS.Translate.toString(transform),
        transition,
        verticalAlign: 'top',
      }}>
      {children}
    </div>
  );
};

const ModifiedDropdown: FC<ModifiedDropdownProps> = React.memo(
  (props: ModifiedDropdownProps) => {
    const {
      debounceTime,
      multiple,
      onChange,
      options: propsOptions,
      search,
      value,
      hideText = false,
    } = props;

    const {
      allowAdditions,
      allowRegexSearch,
      enableReordering,
      itemLimit,
      optionTransform,
      perfAction,
      resultLimit = 100,
      resultLimitMessage = `Limited to ${resultLimit} items. Refine search to see other options.`,
      ...passProps
    } = props;

    const [searchQuery, setSearchQuery] = useState('');
    const [options, setOptions] = useState(propsOptions);

    const doSearch = useMemo(
      () =>
        _.debounce((query: string) => {
          // in multi-select mode, we have to include all the filtered out selected
          // keys or they won't be rendered
          const currentOptions: Option[] = [];
          if (multiple && Array.isArray(value)) {
            const values = value;
            propsOptions.forEach(opt => {
              if (values.find(v => v === opt.value)) {
                currentOptions.push(opt);
              }
            });
          }

          if (search instanceof Function) {
            setOptions(
              _.concat(currentOptions, search(propsOptions, query) as Option[])
            );
          } else {
            const matchedOptions = simpleSearch(propsOptions, query, {
              allowRegexSearch,
            }) as Option[];
            setOptions([...currentOptions, ...matchedOptions]);
          }
        }, debounceTime || 400),
      [allowRegexSearch, debounceTime, multiple, propsOptions, search, value]
    );

    const firstRenderRef = useRef(true);
    const prevDoSearch = usePrevious(doSearch);
    useEffect(() => {
      if (firstRenderRef.current) {
        return;
      }
      if (search !== false) {
        doSearch(searchQuery);
        if (prevDoSearch !== doSearch) {
          prevDoSearch?.cancel();
          doSearch.flush();
        }
      }
      // eslint-disable-next-line
    }, [searchQuery, doSearch, search]);
    useEffect(() => {
      firstRenderRef.current = false;
    }, []);

    const itemCount = useCallback(() => {
      let count = 0;
      if (value != null && Array.isArray(value)) {
        count = value.length;
      }
      return count;
    }, [value]);

    const atItemLimit = useCallback(() => {
      if (itemLimit == null) {
        return false;
      }
      return itemCount() >= itemLimit;
    }, [itemLimit, itemCount]);

    const computedOptions = searchQuery ? options : propsOptions;
    const displayOptions = useMemo(() => {
      const toDisplayOption = (option: Option) =>
        multiple ? option : (getOptionProps(option, hideText) as Option);

      let displayOpts: Option[] = computedOptions
        .slice(0, resultLimit)
        .map(toDisplayOption);
      if (optionTransform) {
        displayOpts = displayOpts.map(optionTransform);
      }

      let selectedVals: StrictDropdownProps['value'] = value;
      if (allowAdditions && searchQuery !== '') {
        selectedVals = searchQuery;
      }

      if (selectedVals != null && (allowAdditions || searchQuery === '')) {
        if (!Array.isArray(selectedVals)) {
          selectedVals = [selectedVals];
        }
        for (const v of selectedVals) {
          if (!displayOpts.some(o => o.value === v)) {
            let option = toDisplayOption(
              computedOptions.find(o => o.value === v) ?? {
                key: v,
                text: v,
                value: v,
              }
            );
            if (optionTransform) {
              option = optionTransform(option);
            }
            displayOpts.unshift(option);
          }
        }
      }

      if (computedOptions.length > resultLimit) {
        displayOpts.push({
          key: ITEM_LIMIT_VALUE,
          text: <span className="hint-text">{resultLimitMessage}</span>,
          value: ITEM_LIMIT_VALUE,
        });
      }

      return perfAction == null
        ? displayOpts
        : displayOpts.map(option => ({
            ...option,
            'data-perf-action': perfAction,
          }));
    }, [
      computedOptions,
      resultLimit,
      searchQuery,
      value,
      allowAdditions,
      hideText,
      multiple,
      optionTransform,
      resultLimitMessage,
      perfAction,
    ]);

    const canReorder = Boolean(multiple && enableReordering);

    const sensors = useSensors(
      // small threshold so clicking a label's delete icon isn't read as a drag
      useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
      useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    // dnd-kit ids; values must be unique once stringified (the current reorder
    // consumers pass unique string keys)
    const sortableIds = useMemo(
      () => (Array.isArray(value) ? value.map(String) : []),
      [value]
    );

    const onReorderDragEnd = (event: DragEndEvent) => {
      const {active, over} = event;
      if (
        !canReorder ||
        onChange == null ||
        !Array.isArray(value) ||
        over == null ||
        active.id === over.id
      ) {
        return;
      }
      const oldIndex = value.findIndex(v => String(v) === String(active.id));
      const newIndex = value.findIndex(v => String(v) === String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      // dnd-kit has no semantic event; reuse the originating activator event
      onChange(
        event.activatorEvent as unknown as React.SyntheticEvent<HTMLElement>,
        {
          value: arrayMove(value, oldIndex, newIndex),
        }
      );
    };

    const renderLabel = (
      item: DropdownItemProps,
      index: number,
      defaultLabelProps: LabelProps
    ) => {
      const onRemove = defaultLabelProps.onRemove!;

      const dragDropLabelStyle: React.CSSProperties = {
        userSelect: 'none',
        padding: '.3125em .8125em',
        margin: 0,
        boxShadow: 'inset 0 0 0 1px rgb(34 36 38 / 15%)',
        fontSize: '1em',
        cursor: 'move',
      };

      const label = (
        <Label
          {...defaultLabelProps}
          style={{
            ...(canReorder ? dragDropLabelStyle : {}),
            position: 'relative',
            paddingRight: 32,
            maxWidth: '100%',
            verticalAlign: 'top',
            wordWrap: 'break-word',
          }}
          className="multi-group-label"
          data-perf-action={perfAction}
          data-test="modified-dropdown-label">
          {item.text}
          <Icon
            style={{
              position: 'absolute',
              right: 13,
              ...(canReorder
                ? {top: '50%', transform: 'translateY(-50%)'}
                : {top: 6}),
            }}
            // stop the drag sensor from hijacking the delete click; the whole
            // pill is the drag handle, so the × must opt out of pointerdown
            onPointerDown={
              canReorder
                ? (e: React.PointerEvent) => e.stopPropagation()
                : undefined
            }
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
              onRemove(e, defaultLabelProps)
            }
            name="delete"
            data-test="modified-dropdown-label-delete"
            data-perf-action={perfAction}
          />
        </Label>
      );

      return canReorder ? (
        <SortableLabel id={String(item.value)}>{label}</SortableLabel>
      ) : (
        label
      );
    };

    const wrapWithDragDrop = (children: React.ReactNode) =>
      canReorder && Array.isArray(value) ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onReorderDragEnd}>
          <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
            {children}
          </SortableContext>
        </DndContext>
      ) : (
        <>{children}</>
      );
    const selectedOption = useMemo(
      () => propsOptions.find(opt => opt.value === value),
      [propsOptions, value]
    );
    const renderTrigger = () => {
      if (searchQuery) {
        return '';
      }
      return (
        <div className="text">
          <OptionWithTooltip
            text={selectedOption ? (selectedOption.text as string) : ''}
          />
        </div>
      );
    };
    return wrapWithDragDrop(
      <Dropdown
        {...passProps}
        data-perf-action={perfAction}
        options={displayOptions}
        lazyLoad
        searchInput={{style: {color: 'black'}, 'data-perf-action': perfAction}}
        selectOnNavigation={false}
        searchQuery={searchQuery}
        search={search !== false ? opts => opts : false}
        // intentionally overrides any consumer-passed renderLabel (it sits after
        // {...passProps}); reorder depends on this wrapping each pill in
        // SortableLabel, so don't move it into passProps
        renderLabel={renderLabel}
        onSearchChange={(e, data) => {
          props.onSearchChange?.(e, data);
          if (!atItemLimit()) {
            setSearchQuery(data.searchQuery);
          }
        }}
        onChange={(e, {value: val}) => {
          setSearchQuery('');
          const valIsArray = Array.isArray(val);
          const valCount = valIsArray ? val.length : 0;

          // HACK: If a multi-select a click on the limit message will append the limiter to the value, make sure to no-op this. A better solution would be to render the limit message as an interactable element, but refactoring this is a much larger task
          const valIsLimit = valIsArray
            ? val.includes(ITEM_LIMIT_VALUE)
            : val === ITEM_LIMIT_VALUE;

          if (valCount < itemCount() || !atItemLimit()) {
            if (onChange && !valIsLimit) {
              onChange(e, {value: val});
            }
          }
        }}
        trigger={renderTrigger()}
        icon={
          passProps.useIcon ? (
            passProps.open ? (
              <IconChevronUp />
            ) : (
              <IconChevronDown />
            )
          ) : undefined
        }
      />
    );
  },
  modifiedDropdownPropsAreEqual
);

export default ModifiedDropdown;

type OptionWithTooltipProps = {
  text: string | null;
};

export const OptionWithTooltip: React.FC<OptionWithTooltipProps> = ({text}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const optionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!optionRef.current) {
      return;
    }
    const isOverflow =
      optionRef.current.scrollWidth > optionRef.current.clientWidth;
    if (isOverflow !== showTooltip) {
      setShowTooltip(isOverflow);
    }
  }, [showTooltip, text]);

  return (
    <div
      ref={optionRef}
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
      {showTooltip ? (
        <TooltipDeprecated
          content={
            <span
              style={{
                display: 'inline-block',
                maxWidth: 300,
                overflowWrap: 'anywhere',
              }}>
              {text}
            </span>
          }
          size="small"
          position="bottom center"
          trigger={<span>{text}</span>}
        />
      ) : (
        text
      )}
    </div>
  );
};
